import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { api } from "@platform/core/api";
import type {
	WebhookDelivery,
	WebhookDeliveryDetail,
	WebhookDeliveryPage,
	WebhookDeliveryStatus,
	WebhookEndpoint,
	WebhookEventPage,
} from "./webhooks.types";

// An organization holds at most 10 endpoints, so one endpoint's page reads it from this list.
export const webhookEndpointsQueryOptions = queryOptions({
	queryKey: ["webhooks"],
	queryFn: () => api.get<WebhookEndpoint[]>("/platform/webhooks"),
});

const PENDING_POLL_MS = 3_000;

// When the log looks again. A send asked for by hand, or an attempt that is due, is looked at
// every few seconds, so a test or a redelivery shows its answer without a reload. A delivery that
// waits hours for its next retry is looked at when that retry is due, not every few seconds until
// then. Nothing pending, no polling.
export function deliveryPollMs(
	deliveries: ReadonlyArray<Pick<WebhookDelivery, "status" | "manualPending" | "nextAttemptAt">>,
	now = Date.now(),
): number | false {
	let wait = Number.POSITIVE_INFINITY;
	for (const delivery of deliveries) {
		if (delivery.status !== "pending") continue;
		if (delivery.manualPending) return PENDING_POLL_MS;
		const due = delivery.nextAttemptAt ? new Date(delivery.nextAttemptAt).getTime() - now : 0;
		wait = Math.min(wait, Math.max(due, PENDING_POLL_MS));
	}
	return Number.isFinite(wait) ? wait : false;
}

export function webhookDeliveriesQueryOptions(
	endpointId: string,
	status: WebhookDeliveryStatus | null,
) {
	return infiniteQueryOptions({
		queryKey: ["webhooks", endpointId, "deliveries", status],
		queryFn: ({ pageParam }) => {
			const query = new URLSearchParams({ limit: "20" });
			if (status) query.set("status", status);
			if (pageParam) query.set("startingAfter", pageParam);
			return api.get<WebhookDeliveryPage>(
				`/platform/webhooks/${endpointId}/deliveries?${query.toString()}`,
			);
		},
		initialPageParam: null as string | null,
		getNextPageParam: (last) => (last.hasMore ? (last.data.at(-1)?.id ?? null) : null),
		refetchInterval: (query) =>
			deliveryPollMs(query.state.data?.pages.flatMap((page) => page.data) ?? []),
	});
}

export function webhookDeliveryQueryOptions(endpointId: string, deliveryId: string) {
	return queryOptions({
		queryKey: ["webhooks", endpointId, "delivery", deliveryId],
		queryFn: () =>
			api.get<WebhookDeliveryDetail>(`/platform/webhooks/${endpointId}/deliveries/${deliveryId}`),
		refetchInterval: (query) => deliveryPollMs(query.state.data ? [query.state.data] : []),
	});
}

// Every event of the organization, across its endpoints, newest first.
export const webhookEventsQueryOptions = infiniteQueryOptions({
	queryKey: ["webhooks", "events"],
	queryFn: ({ pageParam }) => {
		const query = new URLSearchParams({ limit: "20" });
		if (pageParam) query.set("startingAfter", pageParam);
		return api.get<WebhookEventPage>(`/platform/webhooks/events?${query.toString()}`);
	},
	initialPageParam: null as string | null,
	getNextPageParam: (last) => (last.hasMore ? (last.data.at(-1)?.id ?? null) : null),
	refetchInterval: (query) =>
		deliveryPollMs(
			query.state.data?.pages.flatMap((page) => page.data.flatMap((event) => event.deliveries)) ??
				[],
		),
});
